import {
  toUrlFormat,
  avoidNameCollisionsVisitor,
  deleteEmptyCategories,
  addOriginalPathVisitor,
  addLinksAndParentRefVisitor,
  addTypesVisitor,
} from '../../../utils/treeVisitors';
import { pathToTreeKeys } from '../../../utils/applyMenuConfigChanges/deleteMembers';
import { traverseTree } from '../../../utils/traverseTree';
import { STORE_MODES } from './consts';
import get from 'lodash/get';
import { NonExistingTreePathError } from '../../Errors/NonExistingTreePath';
import { AbstractMethodNotImplemented } from '../../Errors/AbstractMethodNotImplemented';
import { hasTypeParams } from '../../../utils/applyMenuConfigChanges/utils';
import { applyMenuConfigChanges } from '../../../utils/applyMenuConfigChanges';
import { ViewerWrapper } from '../../../Viewer/ViewerWrapper';
import { deepCopy } from '../../../utils/objectUtils';
import { createGuidesTree } from '../../../utils/createGuidesTree';
import { removeTrailingSlashes } from '../../../utils/locationUtils';
import {
  isLegacyType,
  LegacySingleType,
  Type,
  TypeParameter,
} from '../../../client/components/TypeDetails/typeParser';
import { Message, Operation } from '../../../types';
import { getTypeSuffix } from '../../../utils/typeUtils';
import { extractType } from '../../../client/components/TypeDetails/utils';
import { isLinkExistInDocs } from '../../Utils/treeUtils';
import { applyTreeVisitors } from '../../Utils/applyTreeVisitors';
import { isVeloSite } from '../../../utils';
import { PARAM_ANNOTATION_STRATEGY } from '../../../client/components/TypeDetails/NestedTable/types';

interface BaseStoreProps {
  experiments?: any;
  baseUrl?: string;
  assetsBaseUrl?: string;
  staticsBaseUrl?: string;
  storePath?: string;
  storeMode: string;
  viewer: ViewerWrapper;
  docsTree?: any;
  id?: string;
}

export class BaseStore {
  experiments: any;
  baseUrl: string;
  assetsBaseUrl: string;
  staticsBaseUrl: string;
  storePath: string;
  storeMode: string;
  guidesTree: any;
  guides: any;
  viewer: ViewerWrapper;
  docsTree: any;
  id: string;

  constructor({
    id,
    baseUrl = '',
    assetsBaseUrl = '',
    staticsBaseUrl = '',
    storePath = '',
    storeMode,
    viewer,
    experiments,
    docsTree = {},
  }: BaseStoreProps) {
    this.id = id;
    this.experiments = experiments;
    this.viewer = viewer;
    this.docsTree = docsTree;
    this.storeMode = storeMode;
    this.storePath = storePath;
    this.baseUrl = baseUrl;
    this.assetsBaseUrl = removeTrailingSlashes(assetsBaseUrl);
    this.staticsBaseUrl = removeTrailingSlashes(staticsBaseUrl);
    return this;
  }

  // eslint-disable-next-line no-unused-vars
  async initStoreData({ docsTree, guides }) {
    throw new AbstractMethodNotImplemented('BaseStore::async initStore');
  }

  async init() {
    throw new AbstractMethodNotImplemented('BaseStore::async init');
  }

  getEndpoint = (uri) => {
    if (uri.startsWith('images')) {
      const assetsUrl = `${this.staticsBaseUrl}/assets/${uri}`;
      if (process.env.NODE_ENV === 'development') {
        console.log(`Getting static assets: ${assetsUrl} From Server`);
      }
      return assetsUrl;
    }

    const assetsBaseUrl =
      removeTrailingSlashes(this.assetsBaseUrl) || `${this.storePath}/assets`;
    const endpoint = `${assetsBaseUrl}/${this.viewer?.id}/${uri}`;
    if (process.env.NODE_ENV === 'development') {
      console.log(`Getting ${uri} From Server`, endpoint);
    }
    return endpoint;
  };

  isPageExistInDocs = (pathname) => {
    const pathnameWithoutTrailingSlashes = removeTrailingSlashes(pathname);

    if (
      pathnameWithoutTrailingSlashes === this.storePath ||
      pathnameWithoutTrailingSlashes === ''
    ) {
      return true;
    }

    const pathWithoutStorePath = pathnameWithoutTrailingSlashes.replace(
      this.storePath,
      '',
    );

    return !!isLinkExistInDocs(this.getFullTree(), pathWithoutStorePath);
  };

  getObjectByStringPath = (matchPath = '') => {
    matchPath = toUrlFormat(matchPath.replace('/', ''));
    const treePath = pathToTreeKeys(matchPath).join('.');
    const node =
      get(this.docsTree, treePath, null) ||
      get(this.guidesTree, treePath, null);

    if (!node) {
      throw new NonExistingTreePathError(treePath);
    }
    return node;
  };

  findMembersByType = (
    type: LegacySingleType | TypeParameter | Type,
  ): {
    currentNode: any;
    memberName: string;
  } => {
    let currentNode = this.docsTree;
    const path = pathToTreeKeys(extractType(type));
    path.forEach((key, i) => {
      const inUrlFormat = toUrlFormat(key);
      if (
        currentNode[key] ||
        (currentNode[inUrlFormat] && i !== path.length - 1)
      ) {
        currentNode = currentNode[key] || currentNode[inUrlFormat];
      }
    });
    const memberName = getTypeSuffix(extractType(type) as string);
    return {
      currentNode,
      memberName,
    };
  };

  getMember = ({
    currentNode,
    memberName,
  }: ReturnType<BaseStore['findMembersByType']>): Message | Operation | {} => {
    if (currentNode.data) {
      return (
        currentNode.data.messages?.find((msg) => msg.name === memberName) ||
        currentNode.data.callbacks?.find((cb) => cb.name === memberName) ||
        {}
      );
    }
    return {};
  };

  getMemberByType = (
    type: Type | LegacySingleType,
  ): ReturnType<BaseStore['getMember']> => {
    if (isLegacyType(type)) {
      if (typeof type === 'string') {
        return this.getMember(this.findMembersByType(type));
      }
      return this.getMember(
        this.findMembersByType(hasTypeParams(type) && type.typeParams[0]),
      );
    }
    if (type.referenceType) {
      return this.getMember(this.findMembersByType(type.referenceType));
    }
    if (type.complexType) {
      return this.getMember(
        this.findMembersByType(
          hasTypeParams(type.complexType) && type.complexType.typeParams[0],
        ),
      );
    }
  };

  hasGuides = () => !!(this.guidesTree && Object.keys(this.guidesTree).length);

  // TODO: remove this function and refactor the places that using it.
  getFullTree = () => {
    const docsTree = this.docsTree || {};
    const guidesTree = this.guidesTree || {};
    return { ...docsTree, ...guidesTree };
  };

  _initDocsTree = (
    docsTree = this.docsTree,
    viewer = this.viewer,
    menuConfig = this.viewer.menuConfig,
  ) => {
    // shouldApplyMenuConfigChanges;
    if (this.storeMode === STORE_MODES.LOCAL_VIEWER) {
      traverseTree(docsTree, [
        addLinksAndParentRefVisitor,
        addTypesVisitor,
        avoidNameCollisionsVisitor(viewer, docsTree),
      ]);
    } else {
      this.timeStart('menu changes');
      applyMenuConfigChanges(docsTree, deepCopy(menuConfig));
      this.timeEnd('menu changes');

      // applyAfterMenuConfigChangesTreeVisitors
      this.timeStart('applyAfterMenuConfigChangesTreeVisitors');

      traverseTree(docsTree, [
        avoidNameCollisionsVisitor(viewer, docsTree),
        addLinksAndParentRefVisitor,
        addTypesVisitor,
      ]);

      deleteEmptyCategories(docsTree);
      this.timeEnd('applyAfterMenuConfigChangesTreeVisitors');
    }

    this.docsTree = docsTree;
  };

  _initGuidesTree = () => {
    this.guidesTree = createGuidesTree(this.guides.items);
    traverseTree(this.guidesTree, [
      addOriginalPathVisitor,
      addLinksAndParentRefVisitor,
      addTypesVisitor,
    ]);
  };

  _initDataForTests = ({ docsTree, guides, guidesTree }) => {
    this.docsTree = docsTree;
    this.guides = guides;
    applyTreeVisitors(docsTree);
    this._initDocsTree();

    if (guidesTree) {
      this.guidesTree = guidesTree;
    } else {
      this._initGuidesTree();
    }
  };

  timeStart(msg) {
    if (process.env.NODE_ENV !== 'test') {
      console.time(`${this.viewer.artifactId} :: ${msg}`);
    }
  }

  timeEnd(msg) {
    if (process.env.NODE_ENV !== 'test') {
      console.timeEnd(`${this.viewer.artifactId} :: ${msg}`);
    }
  }

  getStoreKeyInCDN() {
    if (this.storeMode === STORE_MODES.REGULAR) {
      return `${this.viewer.id}/${this.viewer.currentVersion}`;
    } else if (this.storeMode === STORE_MODES.BRANCH) {
      return `${this.viewer.id}/${this.id}`;
    } else if (this.storeMode === STORE_MODES.LOCAL_VIEWER) {
      return `${this.id}`;
    }
  }
}
